/* eslint-disable max-statements */
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerData, StreamData, StreamType, TimeInMs, UnixTimeMs } from 'Types';
import { useStore } from 'Store';
// esslint-disable-next-line import/no-unresolved -- ts can resolve it
import { STREAM_TYPE } from 'Options';
import { Device, lockToLandscape, lockToPortrait } from 'DeviceUtils';
import { getAssetTitle, isArchive } from 'Utils';
import { TITLE_NUMBERING_EPISODE } from '@24i/st-api-connector/utils';
import {
    ABR_STRATEGY,
    AdvertisementSeek,
    AdvertisementSelection,
    BackgroundMode,
    IPerformanceTweaks,
    ISeekableRange,
    ISeekingEvent,
    ISourceWithoutDRMProvider,
    ITimelineEventPlayed,
    SeekingRestriction,
    SeekType,
    SourcePosterUsage
} from '@24i/player-base';
import { Platform, View, ViewStyle } from 'react-native';
import FadeView from 'LocalGluons/components/FadeView';
import { PlayerManager } from '@24i/react-native-player';
import Animated, { useSharedValue, useAnimatedStyle, runOnJS } from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import appManager, { logEvent } from 'AppManager';
import EPG from 'Utils/epgUtils';
import { t } from 'Locale';
import pubSub from '../../../pubSub';
// eslint-disable-next-line import/no-unresolved -- ts can resolve it
import { ACTION } from '../../../pubSub/actions';
// TODO: Resolve this in TS/ESLINT config..
// eslint-disable-next-line import/no-extraneous-dependencies -- wtf
import PlayerUI from '../PlayerUI';
import usePlayerData from '../PlayerContainer/hooks/usePlayerData';
// import MiniPlayerProgressBar from '../PlayerUI/components/MiniPlayerProgressBar';
import style from './style';
import { shouldSwitchToTimeshift } from '../PlayerContainer/playerUtils';
import { getMediaType } from '../PlayerUI/components/PlayerTracker/utils';
import MiniPlayerDetail from '../PlayerUI/components/MiniPlayerDetails';
import { PlayerUIContainer } from './PlayerUIContainer';
import { PlatformPlayerUI } from './PlatformPlayerUI';
import { buildEngine } from './PlatformNativePlayer';
import { PLAYER_FADE_IN_DURATION } from '../../../options';
import { AdSkipPolicy } from '../PlayerContainer/hooks/useAdvertisements';
import { useConfirmationModal } from '../ConfirmationModal';
import MirroringPreventDialog from '../PlayerContainer/components/MirroringPreventDialog';
import useMirroringCheck from '../PlayerContainer/hooks/useMirroringCheck';
import { useThumbnails } from '../PlayerContainer/hooks/useThumbnails';

interface PlayerUiContainerWithChildrenType {
    style?: ViewStyle;
    children?: any;
}

// eslint-disable-next-line max-len
const PlayerUIContainerWithChildren: FunctionComponent<PlayerUiContainerWithChildrenType> =
    PlayerUIContainer;

const playerEngine = buildEngine();
interface PlayerProps {
    itemStreamData: StreamData;
    playerData: PlayerData;
    onClose: () => void;
    onMiniPlayerPress: () => void;
    isMiniPlayer: boolean;
    getStreamData: (
        startTime?: number,
        customStreamType?: StreamType,
        startPositionType?: SeekType
    ) => void;
}

const BUFFER_LIVE = 10000;
const BUFFER_VOD = 2000;

const Player = ({
    itemStreamData,
    playerData,
    onClose,
    isMiniPlayer,
    onMiniPlayerPress,
    getStreamData
}: PlayerProps): React.ReactElement => {
    const player = useRef<PlayerManager | null>(null);
    const [playerInstance, setPlayerInstance] = useState<PlayerManager | null>(player.current);
    const [isPaused, setPaused] = useState(false);
    const { setPlayerData, deviceStorageState } = useStore();
    const { isMirroring } = useMirroringCheck();
    const [currentTime, setCurrentTime] = useState<UnixTimeMs | TimeInMs | null>(null);
    const [isCasting, setIsCasting] = useState(false);
    const lowLatency = useRef(false);
    const [source, setSource] = useState<ISourceWithoutDRMProvider | undefined>(undefined);
    const [playerLoading, setPlayerLoading] = useState<boolean>(false);
    const { setModalContent } = useConfirmationModal();
    const { setItem } = useThumbnails();
    const {
        modalType,
        setModalType,
        showModal,
        onPause,
        onModalHidden,
        onProgress,
        isVideoBlocked,
        onHorizontalGesture,
        onVideoSeek,
        liveItem,
        refreshDetailItem,
        onBack,
        loading: dataLoading,
        programStart,
        programEnd,
        playNextItem,
        playPreviousItem,
        archiveAvailable,
        nextBingeItem,
        adBlocks,
        fetchAds,
        gracePeriodInSeconds,
        disableForwardSeeking,
        switchToLive: switchToLiveIn,
        setLiveLoading,
        liveLoading,
        isModalBinge,
        isLiveVOD,
        changeItem,
        setNewCurrentItem,
        adSkipPolicy,
        mediaHouse,
        updateBingeItem
    } = usePlayerData(
        setCurrentTime,
        currentTime,
        playerData,
        itemStreamData,
        player.current,
        playerInstance,
        onClose,
        () => { }
    );
    const { offline, item } = playerData;
    const [fadeInFinish, setFadeInFinish] = useState(false);
    const [progress, setProgress] = useState<
        | {
            progress: number;
            end: number;
            timeString: string;
        }
        | boolean
    >(false);
    const [canWatch, setCanWatch] = useState(false);
    const { streamUrl, streamType, maxTimeshift, startTime, startPositionType } = itemStreamData;
    const scale = useSharedValue(1);
    const savedScale = useSharedValue(1);
    const continueInBackgroundMode = Platform.select({
        ios: BackgroundMode.PICTURE_IN_PICTURE,
        default: BackgroundMode.PAUSE
    });

    const switchToLive = () => {
        switchToLiveIn();
    };

    useEffect(() => {
        setItem(item);
    }, [item]);

    const popStateListener = useCallback(() => {
        onBack();
    }, [onBack]);

    useEffect(() => {
        window.history.pushState(null, '', `${window.location.href}?player=true`);
        window.addEventListener('popstate', popStateListener);

        const timeout = setTimeout(() => {
            setFadeInFinish(true);
        }, PLAYER_FADE_IN_DURATION);
        return () => {
            window.removeEventListener('popstate', popStateListener);
            clearTimeout(timeout);
            if (player.current) {
                player.current.unload();
            }
        };
    }, []);
    useEffect(() => {
        const fetchBriefChannel = async () => {
            const newItem = await appManager
                .getApi()
                .fetchChannelBrief({ list: EPG.DEFAULT_LIST_MODE }, item.getChannelId());
            lowLatency.current = newItem?.lowLatency ?? false;
        };
        fetchBriefChannel();
    }, [playerData.item]);

    useEffect(() => {
        if (isMirroring) {
            lockToPortrait();
            setModalContent(
                <MirroringPreventDialog setModalContent={setModalContent} onClose={onClose} />
            );
        } else {
            lockToLandscape();
            setModalContent(null);
        }
    }, [isMirroring]);

    const onVideoProgress = useCallback(async () => {
        if (!player.current) return;
        const {
            paused: isPaused,
            bufferedRanges,
            duration,
            currentTime: currTime
        } = player.current;
        const progressData = {
            isPaused,
            progressData: {
                currentTime: currTime,
                playableDuration:
                    player.current &&
                        bufferedRanges instanceof Array &&
                        bufferedRanges[0] &&
                        bufferedRanges[0].end
                        ? bufferedRanges[0].end
                        : 0
            },
            duration: Device.isAndroid
                ? duration
                : player?.current?.source?.metadata?.programEnd -
                player?.current?.source?.metadata?.programStart
        };
        pubSub.dispatch(ACTION.ON_VIDEO_PROGRESS, progressData);
        onProgress(progressData);
    }, [onProgress]);

    const onTimeUpdate = useCallback(
        (event: { currentTime: number }) => {
            onVideoProgress();
            if (!liveLoading) setCurrentTime(event.currentTime);
        },
        [onVideoProgress]
    );

    const onCastingStarted = () => setIsCasting(true);
    const onCastingEnded = () => setIsCasting(false);

    const onItemChange = (newItem: PlayerData['item']) => {
        setCurrentTime(0);
        setLiveLoading(true);
        setPlayerData({ item: newItem });
    };

    // TODO: Will be done later
    const onVideoQualityChanged = () => {
        // let startTime = currentTime;
        // if (Device.isWeb && streamType === STREAM_TYPE.TIMESHIFT) {
        //     startTime = Math.abs(startTime - this.player.getSeekableRange().end);
        // }
        // if (!offline) {
        //     getStreamData(streamType, startTime);
        // }
    };

    // const switchToLive = async () => {
    //     // TODO: Handle audio tracks
    //     //     selectedAudioTrack: {
    //     //         type: 'disabled'
    //     //     }
    //     getStreamData(0, STREAM_TYPE.LIVE, SeekType.TIMESHIFT);
    // };

    const onVideoEnd = () => {
        if (!player.current) return;
        if (
            streamType &&
            !isModalBinge &&
            streamType !== STREAM_TYPE.ARCHIVE &&
            !(streamType === 'LIVE' && playerInstance?.live === false) // hack for diferenciating of synthetic channels
        ) {
            // there is an issue with bingeWatching
            // BingeWatchingModal close sooner than the video finish
            // This does not affect the watching due to loadings
            setLiveLoading(false);
            player.current.pause();
            onBack();
        } else {
            setLiveLoading(true);
        }
        // if (
        //     streamType &&
        //     shouldReturnBackWhenVideoEnds(playerData.item, streamType) &&
        //     modalType !== playerModalsEnum.bingeWatching
        // ) {
        //     onBack();
        // } else {
        //     player.current.pause();
        // }
    };

    const switchToTimeshift = (timeshift?: number, startTimeType?: SeekType) => {
        // setCanWatch(false);
        // player.current?.unload();
        // @ts-ignore
        if (!offline) {
            getStreamData(timeshift, STREAM_TYPE.ARCHIVE, startTimeType);
            updateBingeItem();
        }
    };

    const onDurationPress = () => {
        if (streamType === STREAM_TYPE.ARCHIVE && !offline) {
            // @ts-ignore
            if (item.getMetaData()?.detail?.seekingForbidden === true) {
                const { seekableRange } = playerInstance;
                player.current.seek(seekableRange.end);
                return;
            }
            switchToLive();
        }
    };

    const onSeek = async (seekEvent: ISeekingEvent) => {
        if (player.current) {
            const { duration } = player.current;
            onVideoSeek(seekEvent.time, duration, seekEvent.type);
        }
    };

    const onVideoPaused = async () => {
        if (!player.current) return;

        const { duration } = player.current;
        onPause(currentTime || 0, duration);
    };

    const onResumePlaying = () => {
        if (!player.current) return;
        player.current.play();
    };

    const onCurrentTimePress = async () => {
        if (!player.current || !streamType) return;
        const seekableRange: ISeekableRange = player.current.seekableRange || {
            start: 0,
            end: 0
        };
        const timeshiftOffset = Device.isWeb ? seekableRange.end - (maxTimeshift || 0) : 1;

        if (shouldSwitchToTimeshift(playerData?.item, streamType, offline)) {
            switchToTimeshift(0, SeekType.UTC);
        } else if (streamType === STREAM_TYPE.ARCHIVE) {
            player.current.seek(seekableRange.start || 0);
        }
    };

    const onSeekedOutOfDVR = useCallback(
        ({ time, seekableRange }: { time: number; seekableRange: ISeekableRange }) => {
            if (!player.current) return;

            // No described behaviour for this case
            if (
                streamType !== STREAM_TYPE.LIVE &&
                streamType !== STREAM_TYPE.ARCHIVE &&
                playerData?.item
            )
                return;

            if (time > seekableRange.end) return;

            if (shouldSwitchToTimeshift(playerData?.item, streamType, offline)) {
                const timestamp = player.current.getUTCTimestamp(time)?.getTime();
                if (timestamp) {
                    switchToTimeshift(timestamp, SeekType.UTC);
                } else {
                    switchToTimeshift(time - seekableRange.end, SeekType.TIMESHIFT);
                }
            }
        },
        [player.current, playerData, streamType, offline]
    );

    useEffect(() => {
        const playerElement = player.current;
        // eslint-disable-next-line react/display-name
        if (!playerElement) return () => null;
        playerElement?.addEventListener?.('seekingoutofdvr', onSeekedOutOfDVR);
        playerElement?.addEventListener?.('timeupdate', onTimeUpdate);
        return () => {
            if (!playerElement) return;
            playerElement?.removeEventListener?.('seekingoutofdvr', onSeekedOutOfDVR);
            playerElement?.removeEventListener?.('timeupdate', onTimeUpdate);
        };
    }, [onSeekedOutOfDVR, onTimeUpdate]);

    const pausePlay = () => {
        if (!player.current) return;

        if (player.current.paused) {
            onResumePlaying();
            player.current.play();
        } else {
            player.current.pause();
        }
    };

    const isSeekingForbidden = !!itemStreamData.streamData?.seekingForbidden;

    useEffect(() => {
        if (player.current) {
            let advertisementSelection = AdvertisementSelection.NONE;
            if (adSkipPolicy === AdSkipPolicy.NOT_SKIPPABLE) {
                advertisementSelection = AdvertisementSelection.ALL;
            }
            if (adSkipPolicy === AdSkipPolicy.PARTIAL_SKIPPABLE) {
                advertisementSelection = AdvertisementSelection.LAST;
            }
            if (lowLatency.current) {
                player.current.seekingRestrictions = {
                    ...player.current.seekingRestrictions,
                    unskippableSeek: SeekingRestriction.NO_SEEKING,
                    skippableSeek: SeekingRestriction.NO_SEEKING,
                    contentSeek: SeekingRestriction.NO_SEEKING,
                };
            } else if (disableForwardSeeking) {
                player.current.seekingRestrictions = {
                    advertisementSkippablePersists: false,
                    skippableSeek: SeekingRestriction.NO_FORWARD,
                    unskippableSeek: SeekingRestriction.NO_FORWARD,
                    advertisementSeek: AdvertisementSeek.UNRESTRICTED,
                    advertisementFilter: ITimelineEventPlayed.PLAYED,
                    advertisementSelection,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    advertisementSnapback: false,
                    ...player.current.seekingRestrictions,
                    contentSeek: SeekingRestriction.NO_FORWARD
                };
            } else {
                player.current.seekingRestrictions = {
                    advertisementSkippablePersists: false,
                    skippableSeek: adSkipPolicy === AdSkipPolicy.NOT_SKIPPABLE ?
                        SeekingRestriction.NO_FORWARD : SeekingRestriction.UNRESTRICTED,
                    unskippableSeek: adSkipPolicy === AdSkipPolicy.SKIPPABLE ?
                        SeekingRestriction.UNRESTRICTED : SeekingRestriction.NO_FORWARD,
                    advertisementSeek: AdvertisementSeek.UNRESTRICTED,
                    advertisementFilter: ITimelineEventPlayed.PLAYED,
                    advertisementSelection,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    advertisementSnapback: false,
                    ...player.current.seekingRestrictions,
                    contentSeek: SeekingRestriction.UNRESTRICTED
                };
            }
        }
    }, [player.current, disableForwardSeeking, adSkipPolicy, lowLatency.current]);

    const processSource = async () => {
        const timeline = await fetchAds();
        const image = item.getImageUrl('POSTER', 'HIGH') || item.getImageUrl('PREVIEW', 'HIGH');
        scale.value = 1;
        savedScale.value = 1;
        setSource({
            url: streamUrl,
            mimeType: getMediaType(streamUrl),
            posters:
                Device.isWeb || !image
                    ? null
                    : [
                        {
                            url: image,
                            usage: SourcePosterUsage.MEDIA_CONTROL
                        }
                    ],
            timeline,
            // TODO: add streamtype check and move to usePlayerData hook
            metadata: {
                title: item.getTitle(),
                programStart,
                programEnd,
                timeline
            },
            lowLatency: lowLatency.current
        });

        setCanWatch(true);
        // setProgress(false);
        setLiveLoading(false);
    };

    useEffect(() => {
        setSource(undefined);
        setCanWatch(false);
        processSource();
    }, [streamUrl]);

    // useEffect(() => {
    //     if (adBlocks === undefined)
    //         return;
    //     setSource({
    //         url: streamUrl,
    //         mimeType: getMediaType(streamUrl),
    //         timeline: adBlocks,
    //         // TODO: add streamtype check and move to usePlayerData hook
    //         metadata: {
    //             programStart,
    //             programEnd,
    //             timeline: adBlocks
    //         }
    //     });

    //     setCanWatch(true);

    // }, [adBlocks]);

    const performanceTweaks: IPerformanceTweaks = useMemo(
        () => ({
            restartBuffer: streamType === 'LIVE' ? BUFFER_LIVE : BUFFER_VOD
        }),
        [streamType]
    );

    const setVideoPlayerRef = useCallback((ref: PlayerManager) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- playerInstance is weirdly typed
        player.current = ref;
        setPlayerInstance(ref);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- USEFUL FOR DEBUG
        window.player = ref;
        if (ref) {
            ref.performanceTweaks = performanceTweaks;
            ref.continueInBackgroundMode = continueInBackgroundMode;
        }
    }, []);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (player.current?.playing) {
            setLiveLoading(false);
            if (progress) {
                timeout = setTimeout(() => {
                    setProgress(false);
                }, 1000);
            }
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [player.current?.playing]);

    // todo check!
    // }, [player.current?.playing, progress]);

    const logWrapper = (args) => {
        logEvent(args);
    };

    const pinchGesture = Gesture.Pinch()
        .onUpdate((e) => {
            let value = savedScale.value * e.scale;
            value = value > 2 ? 2 : value;
            value = value < 1 ? 1 : value;
            scale.value = value;
        })
        .onEnd(() => {
            savedScale.value = scale.value;
            runOnJS(logWrapper)({
                type: 'user-flow',
                subtype: 'player',
                content1: 'maxiplayer',
                content2: 'zoom-pinch',
                context: '/player'
            });
        });

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ scale: scale.value }]
    }));

    const setScale = () => {
        let { value } = scale;
        if (value >= 1.35) {
            value = 1;
        } else if (value < 1.35) {
            value = 1.35;
        }
        scale.value = value;
        savedScale.value = value;
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'zoom-button',
            context: '/player'
        });
    };

    const [enabledSaver, setEnabledSaver] = useState(false);
    const checkForDataSaver = async () => {
        if (Device.isWeb) return;

        const enabled = await appManager.getDataSaverValueFromStorage();
        // @ts-ignore
        setEnabledSaver(enabled.data.enableSaver);
        // @ts-ignore
        return enabled.data.enableSaver;
    };

    const switchFromLowLatency = async () => {
        const api = appManager.getApi();
        setModalType(null);
        logEvent({
            time: Date.now(),
            type: 'user-flow',
            subtype: 'low-latency-popup',
            deviceId: '0000',
            deviceType: 'OTT_TV_ANDROID',
            clientId: '0000',
            ipaddress: '',
            content1: 'switch',
            content2: '',
            context: '/player'
        });
        const newLiveItem = await api.fetchProgramDetail({
            channelId: t('LOWLATENCY.WORKAROUND.CHANNELID')
        });
        setNewCurrentItem(newLiveItem, true);
    };

    useEffect(() => {
        const fetchDataSaver = async () => {
            const dataSaverEnabled = await checkForDataSaver();
            if (playerInstance) {
                if (dataSaverEnabled) {
                    playerInstance.trackRestrictions = { maxBitrate: 1600000 };
                } else {
                    playerInstance.trackRestrictions = { maxBitrate: 2400000 };
                }
            }
        };

        fetchDataSaver();
    }, [playerInstance, enabledSaver]);

    const startTimePosition = useMemo(
        () =>
            isArchive(playerData.item) && Device.isWeb
                ? itemStreamData.startTime + 1000
                : itemStreamData.startTime, // skip 1 second for archive to avoid hiting gap in player breaking the stream
        [startTime]
    );

    return (
        <>
            {!isMirroring && (
                <GestureDetector gesture={pinchGesture}>
                    <Animated.View
                        style={isMiniPlayer ? style.miniVideoPlayer : style.videoPlayer}
                        nativeID="player"
                    >
                        {canWatch && (
                            <Animated.View style={[{ flex: 1 }, animatedStyle]}>
                                <PlatformPlayerUI
                                    canWatch={canWatch}
                                    // key={playerKey}
                                    engine={playerEngine}
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore -- engine is incorrectly typed
                                    onSeeking={onSeek}
                                    source={source}
                                    setVideoPlayerRef={setVideoPlayerRef}
                                    style={isMiniPlayer ? style.miniVideoPlayer : style.videoPlayer}
                                    startPosition={startTimePosition}
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore -- engine is incorrectly typed //TOD: Fix this=there is 1s skip due to dash fix of gap configuration
                                    startPositionType={startPositionType}
                                    performanceTweaks={performanceTweaks}
                                    onEnd={onVideoEnd}
                                    // @ts-ignore
                                    onCastingStarted={onCastingStarted}
                                    onCastingEnded={onCastingEnded}
                                    onPause={onVideoPaused}
                                    onCanPlay={() => playerLoading && setPlayerLoading(false)}
                                    onWaiting={() => !playerLoading && setPlayerLoading(true)}
                                    autoPlay
                                    abrStrategy={ABR_STRATEGY.QUALITY}
                                    onError={(error) => {
                                        console.log('Error Player.tsx', error);
                                    }}
                                    continueInBackgroundMode={continueInBackgroundMode}
                                />
                            </Animated.View>
                        )}

                        {(!Device.isWeb || playerInstance) && (
                            <View
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0
                                }}
                            >
                                <FadeView duration={fadeInFinish ? 0 : PLAYER_FADE_IN_DURATION}>
                                    <PlayerUIContainerWithChildren>
                                        <PlayerUI
                                            onNextPress={playNextItem}
                                            onPreviousPress={playPreviousItem}
                                            isMiniPlayer={isMiniPlayer}
                                            isCasting={isCasting}
                                            itemStreamData={
                                                (itemStreamData || {
                                                    streamType: '',
                                                    offline: '',
                                                    maxTimeshift: ''
                                                }) as any
                                            }
                                            playerInstance={playerInstance}
                                            item={playerData.item}
                                            loading={dataLoading || playerLoading}
                                            liveItem={liveItem}
                                            onResumePlaying={onResumePlaying}
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore -- playerInstance is weirdly typed */}
                                            onPause={onPause}
                                            showModal={showModal}
                                            onModalHidden={onModalHidden}
                                            onProgress={onProgress}
                                            isVideoBlocked={isVideoBlocked}
                                            onHorizontalGesture={onHorizontalGesture}
                                            onVideoSeek={onVideoSeek}
                                            switchToLive={switchToLive}
                                            refreshDetailItem={refreshDetailItem}
                                            onBack={onBack}
                                            onMiniPlayerPress={onMiniPlayerPress}
                                            modalType={modalType}
                                            setModalType={setModalType}
                                            onVideoQualityChanged={onVideoQualityChanged}
                                            onItemChange={onItemChange}
                                            currentTime={currentTime}
                                            onDurationPress={onDurationPress}
                                            onCurrentTimePress={onCurrentTimePress}
                                            archiveAvailable={archiveAvailable}
                                            nextBingeItem={nextBingeItem}
                                            gracePeriodInSeconds={gracePeriodInSeconds}
                                            setProgress={setProgress}
                                            progress={progress}
                                            setLiveLoading={setLiveLoading}
                                            liveLoading={dataLoading}
                                            isModalBinge={isModalBinge}
                                            isLiveVOD={isLiveVOD}
                                            changeItem={changeItem}
                                            onClose={onClose}
                                            setNewCurrentItem={setNewCurrentItem}
                                            adSkipPolicy={adSkipPolicy}
                                            mediaHouse={mediaHouse}
                                            switchToTimeshift={switchToTimeshift}
                                            source={source}
                                            scale={scale.value}
                                            setScale={setScale}
                                            lowLatency={lowLatency.current}
                                            switchFromLowLatency={switchFromLowLatency}
                                            isPaused={isPaused}
                                            setPaused={setPaused}
                                        />
                                    </PlayerUIContainerWithChildren>
                                </FadeView>
                            </View>
                        )}

                        {isMiniPlayer && player.current && (
                            <MiniPlayerDetail
                                key="MiniPlayerDetail"
                                style={style.miniPlayerDetail}
                                onPlayPausePress={pausePlay}
                                onClose={onBack}
                                title={getAssetTitle(playerData.item, TITLE_NUMBERING_EPISODE)}
                                // MCore Compatibility fallback
                                subtitle={playerData.item.getMetaData().getChannelName() || ''}
                                isPaused={player.current.paused}
                            />
                        )}
                    </Animated.View>
                </GestureDetector>
            )}
        </>
    );
};
export default Player;
